<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import * as echarts from 'echarts/core';
  import { BarChart } from 'echarts/charts';
  import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components';
  import { CanvasRenderer } from 'echarts/renderers';
  import { epitomeTheme } from '../theme.js';

  echarts.use([BarChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer]);

  let registered = false;
  function ensureTheme() {
    if (!registered) {
      echarts.registerTheme('epitome', epitomeTheme);
      registered = true;
    }
  }

  interface Props {
    categories: string[];
    series: { name: string; data: number[] }[];
    height?: string;
    orientation?: 'vertical' | 'horizontal';
    yFormatter?: ((v: number) => string) | null;
    stack?: string | null;
    legend?: boolean;
  }

  let {
    categories,
    series,
    height = '320px',
    orientation = 'vertical',
    yFormatter = null,
    stack = null,
    legend = true,
  }: Props = $props();

  let el: HTMLDivElement;
  let chart: echarts.ECharts | undefined;

  const option = $derived(() => {
    const isHorizontal = orientation === 'horizontal';
    const catAxis = {
      type: 'category' as const,
      data: categories,
    };
    const valAxis = {
      type: 'value' as const,
      axisLabel: yFormatter ? { formatter: (v: number) => yFormatter(v) } : {},
    };
    return {
      legend: legend ? { show: true } : { show: false },
      xAxis: isHorizontal ? valAxis : catAxis,
      yAxis: isHorizontal ? catAxis : valAxis,
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      series: series.map((s) => ({
        name: s.name,
        type: 'bar',
        data: s.data,
        stack: stack ?? undefined,
      })),
    };
  });

  onMount(() => {
    ensureTheme();
    chart = echarts.init(el, 'epitome');
    chart.setOption(option());
    const ro = new ResizeObserver(() => chart?.resize());
    ro.observe(el);
    return () => ro.disconnect();
  });

  $effect(() => {
    if (chart) chart.setOption(option(), { notMerge: false });
  });

  onDestroy(() => chart?.dispose());
</script>

<div bind:this={el} class="chart" style="height: {height}"></div>

<style>
  .chart {
    width: 100%;
    min-height: 200px;
  }
</style>
